import { Button, Tooltip } from 'antd'
import { CoordinateInputTablePointAndClickApi } from 'components/form/inputs/coordinatesInput/CoordinatesInputTable'
import { Coordinate } from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import { AssetModelComponentClassMap } from 'features/assetModeling'
import { AssetEditorState } from 'features/rightDrawer/drawerStates/assetEditorState'
import { FeatureSelectionPage } from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/features/FeatureSelectionPage'
import startCase from 'lodash/startCase'
import React, { useState } from 'react'
import styled from 'styled-components'
import {
  assetModelAvailableComponents,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponent,
  GeckoComponentModelType,
  GeckoUnit,
} from 'types'
import { UpdaterFunction } from 'types/generics/updaterFunction'
import { noOp } from 'utils/noOp'

const StyledComponentButtonDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const StyledGeckoAssetModelComponentButton = styled(Button)`
  width: 100%;
  height: 60px;
`

const StyledParagraph = styled.div`
  padding: 0.625rem 0;
`

interface EditRichAssetModelProps {
  assetModel: GeckoAssetModel
  component: GeckoComponent
  refreshData: () => Promise<void>
  unit: GeckoUnit | undefined
  goToEditComponent?: () => void
  goToSelectComponent?: () => void
  changeDrawerState?: (changeDrawerState: AssetEditorState) => void
  selectedFeatureId: string | undefined
  onFeatureSelected: UpdaterFunction<string | undefined> | null
  pointAndClickApi: CoordinateInputTablePointAndClickApi | undefined
  highlightCoordinate: (coordinate: Coordinate | null) => void
  displayCoordinateUpdatesApi:
    | {
        display: (newCoords: ReadonlyArray<Coordinate>) => void
        clear: () => void
      }
    | undefined
  jsonSelector?: React.ReactNode
  /** Optional per-field accessory for the Info-tab component form (keyed by dataKey). */
  renderFieldAccessory?: (dataKey: string) => React.ReactNode
  /** Optional callback fired (and awaited) after the Info-tab value save persists. */
  onSaved?: () => void | Promise<void>
}

export const EditGeckoRichAssetModel = ({
  assetModel,
  component,
  refreshData,
  unit,
  goToEditComponent,
  goToSelectComponent,
  jsonSelector,
  selectedFeatureId,
  onFeatureSelected,
  pointAndClickApi,
  highlightCoordinate,
  displayCoordinateUpdatesApi = {
    display: noOp,
    clear: noOp,
  },
  renderFieldAccessory,
  onSaved,
}: EditRichAssetModelProps) => {
  // Double check to make sure the Asset Model type is part of the supported list
  if (!Object.values(GeckoAssetModelType).includes(assetModel.data.type)) {
    throw new Error(
      `Unexpected Asset Model Type (${assetModel.data.type}). Not found in GeckoAssetModelType!`
    )
  }

  const [selectedComponentType, setSelectedComponentType] = useState<
    GeckoComponentModelType | undefined
  >(undefined)

  // If this component hasn't been added to the rich asset model yet, and we can't
  // automatically determine what it is, ask the user
  if (!assetModel.data.components.has(component.id) && !selectedComponentType) {
    const availableComponents = () => {
      return assetModelAvailableComponents(assetModel.data.type).map(
        (component_type) => {
          const componentClass = AssetModelComponentClassMap.get(component_type)
          return (
            <Tooltip
              key={component_type}
              title={
                !componentClass
                  ? `This component type (${startCase(component_type)}) hasn't been defined as an editable component yet. Please reach out to the Enterprise team with any questions! `
                  : undefined
              }>
              <StyledGeckoAssetModelComponentButton
                key={component_type}
                onClick={() => {
                  setSelectedComponentType(
                    component_type as GeckoComponentModelType
                  )
                }}
                disabled={!componentClass}>
                {startCase(component_type)}
              </StyledGeckoAssetModelComponentButton>
            </Tooltip>
          )
        }
      )
    }

    return (
      <>
        <h4>Select Component Type</h4>
        <StyledParagraph>
          In order to edit this component, we need to define a rich asset model
          for it. The following options are available for a{' '}
          {startCase(assetModel.data.type)}:
        </StyledParagraph>
        <StyledComponentButtonDiv>
          {availableComponents()}
        </StyledComponentButtonDiv>
        <StyledParagraph>
          Don&apos;t see an appropriate component type here?
          <div>
            <a
              href={
                'https://geckorobotics.enterprise.slack.com/archives/C061S6F5G0G'
              }
              target={'_blank'}
              rel='noreferrer'>
              Reach out to the Enterprise team on Slack!
            </a>
          </div>
        </StyledParagraph>
        {goToEditComponent && (
          <StyledParagraph>
            Not ready to make a rich asset model for this component?
            <div>
              <a onClick={goToEditComponent}>
                Edit this component directly here.
              </a>
            </div>
          </StyledParagraph>
        )}
      </>
    )
  } else if (
    assetModel.data.components.has(component.id) &&
    !selectedComponentType
  ) {
    const assetModelComponent = assetModel.data.components.get(component.id)
    if (assetModelComponent && assetModelComponent.type) {
      setSelectedComponentType(assetModelComponent.type)
    } else {
      throw new Error('Unexpected Component. No type defined!')
    }
    return <div>Loading...</div>
  }

  // By the time we reach this code, we should have a component type defined
  if (!selectedComponentType) {
    throw new Error(
      'Unexpected State. Asset Model does not contain the component, and no Component Type was selected!'
    )
  }

  // Otherwise, we should be displaying the feature selection page to the user
  return (
    <FeatureSelectionPage
      assetModel={assetModel}
      component={component}
      unit={unit}
      refreshData={refreshData}
      goToSelectComponent={goToSelectComponent}
      jsonSelector={jsonSelector}
      selectedFeatureId={selectedFeatureId}
      onFeatureSelected={onFeatureSelected}
      selectedComponentType={selectedComponentType}
      pointAndClickApi={pointAndClickApi}
      highlightCoordinate={highlightCoordinate}
      displayCoordinateUpdatesApi={displayCoordinateUpdatesApi}
      renderFieldAccessory={renderFieldAccessory}
      onSaved={onSaved}
    />
  )
}
